// TODO: THIS IS BETTER IMPLEMENTED AS JUST AN EXPOSED UTIL FUNCTION 
// THAT CAN BE CONSUMED BY USERS IN A FUNCTION NODE



/**
 * ArrayShuffleNode - Generates shuffled indices [0..length-1], outputs first `count`
 *
 * Uses seeded Fisher-Yates shuffle for deterministic, reproducible output.
 * Connect `length` to a source like subdiv.count to auto-size.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { params, internals } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import { seededRandom } from '@/plugins/randomUtils';

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const arrayShuffleEndpoints = {
  ...params({
    seed:   { value: 42, min: 0, max: 9999, step: 1 },
    count:  { value: 3, min: 0, max: 256, step: 1 },
    length: { value: 9, min: 0, max: 256, step: 1, isInput: true },
  }),
  ...internals({
    output:      { dataType: 'array', isOutput: true, label: 'Indices' },
    outputCount: { dataType: 'number', isOutput: true, label: 'Actual Count' },
  }),
};

// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

class ArrayShuffleNode extends BaseNode<typeof arrayShuffleEndpoints> {

  initialize(): void {
    this.compute();
  }

  onParamChanged(_payload: Payload, _endpointId: string): void {
    this.compute();
  }

  private compute(): void {
    const length = this.params.length.value;
    const count = Math.min(this.params.count.value, length);
    const seed = this.params.seed.value;

    // Build index array [0..length-1]
    const indices = Array.from({ length }, (_, i) => i);

    // Seeded Fisher-Yates shuffle
    const rng = seededRandom(seed);
    for (let i = length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }

    this.internals.output.set(indices.slice(0, count));
    this.internals.outputCount.set(count);
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const arrayShuffleNodeDef: NodeDef<ArrayShuffleNode> = {
  type: 'ArrayShuffle',
  shortName: 'shuffle',
  info: 'Seeded Fisher-Yates shuffle. Generates indices [0..length-1] in random order, outputs the first count.',
  domain: Domain.Data,
  category: 'Array',
  endpoints: arrayShuffleEndpoints,
  ctor: ArrayShuffleNode,
};
